import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import z from "zod";

function parseSearchParams<T>(
  schema: z.ZodType<T>,
  defaultValue: T,
  searchParams: URLSearchParams,
) {
  const queryObject: Record<string, string | undefined> = {};

  for (const [key, value] of searchParams.entries()) {
    queryObject[key] = value;
  }

  const result = schema.safeParse(queryObject);

  if (result.error) {
    return defaultValue;
  }

  return result.data;
}

export function useTypedSearchParams<
  T extends { [key: string]: string },
  S extends z.ZodTypeAny,
>(schema: S, defaultValue: T) {
  const [searchParams, setSearchParams] = useSearchParams(defaultValue);

  const state = useMemo(
    () => parseSearchParams(schema, defaultValue, searchParams),
    [schema, defaultValue, searchParams],
  );

  const setState = useCallback(
    (
      stateOrStateFn: T | ((prev: T) => T),
      { replace }: { replace: boolean } = { replace: false },
    ) => {
      setSearchParams(
        (prev) => {
          let state;
          if (typeof stateOrStateFn === "function") {
            state = stateOrStateFn(
              parseSearchParams(schema, defaultValue, prev),
            );
          } else {
            state = stateOrStateFn;
          }

          const params = new URLSearchParams(prev);

          for (const [key, value] of Object.entries(state)) {
            if (value === null) {
              params.delete(key);
            } else {
              params.set(key, value);
            }
          }

          return params;
        },
        { replace },
      );
    },
    [setSearchParams, schema, defaultValue],
  );

  return [state, setState] as const;
}
